// src/contexts/AuthContext.jsx
import React, { createContext, useContext, useState, useEffect } from 'react';
import { auth, db } from '../pages/app/protected/firebase';
import { doc, getDoc } from 'firebase/firestore';

const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [companyId, setCompanyId] = useState(null);
  const [role, setRole] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const unsubscribe = auth.onAuthStateChanged(async (authUser) => {
      try {
        if (authUser) {
          // Check if user is verified
          await authUser.reload();
          
          // Fetch user document to get company ID
          const userDoc = await getDoc(doc(db, 'users', authUser.uid));
          const userData = userDoc.data();
          
          setUser({
            ...authUser,
            emailVerified: authUser.emailVerified
          });
          
          if (userData?.companyId) {
            setCompanyId(userData.companyId);
          }
          if (userData?.role) {
            setRole(userData.role);
          }
        } else {
          setUser(null);
          setCompanyId(null);
          setRole(null);
        }
      } catch (error) {
        console.error('Error checking auth state:', error);
        setUser(null);
        setCompanyId(null);
        setRole(null);
      } finally {
        setLoading(false);
      }
    });

    return () => unsubscribe();
  }, []);

  const value = {
    user,
    companyId,
    role,
    loading,
    isAuthenticated: !!user,
    emailVerified: user?.emailVerified || false,
    checkEmailVerification: async (email) => {
      try {
        const userRecord = await auth.fetchSignInMethodsForEmail(email);
        return userRecord.length > 0;
      } catch (error) {
        console.error('Error checking email verification:', error);
        return false;
      }
    },
    updateRememberMe: (email) => {
      localStorage.setItem('cashmate-remember-me', email);
    },
    clearRememberMe: () => {
      localStorage.removeItem('cashmate-remember-me');
    }
  };

  return (
    <AuthContext.Provider value={value}>
      {!loading ? children : <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-white"></div>
      </div>}
    </AuthContext.Provider>
  );
};

export default AuthContext;